import { create } from 'zustand';

const useCanvasStore = create((set, get) => ({
  mapId: null,
  mapTitle: '',
  mode: 'edit',
  nodes: [],
  edges: [],
  selectedNodeId: null,
  selectedEdgeId: null,
  sidebarTab: null,
  nodeChats: {},
  nodeMastery: {},
  isDirty: false,
  canvasBackground: '#ffffff',

  setMode: (mode) => set({ mode }),
  toggleMode: () => set(s => ({ mode: s.mode === 'edit' ? 'learn' : 'edit' })),
  setNodes: (nodes) => set({ nodes, isDirty: true }),
  setEdges: (edges) => set({ edges, isDirty: true }),
  
  // Real-time collaboration: update single node position without triggering isDirty
  updateNodePosition: (nodeId, position) => set(state => ({
    nodes: state.nodes.map(n =>
      n.id === nodeId ? { ...n, position } : n
    )
  })),
  
  // Real-time collaboration: update single node label without triggering isDirty
  updateNodeLabel: (nodeId, label) => set(state => ({
    nodes: state.nodes.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, label } } : n
    )
  })),
  
  // Real-time collaboration: batch update nodes and edges from remote
  setNodesAndEdges: (nodes, edges) => set({ nodes, edges }),
  
  setSelectedNodeId: (id) => set({ selectedNodeId: id, selectedEdgeId: null }),
  setSelectedEdgeId: (id) => set({ selectedEdgeId: id, selectedNodeId: null }),
  setSidebarTab: (tab) => set({ sidebarTab: tab }),
  closeSidebar: () => set({ sidebarTab: null }),
  setMapTitle: (title) => set({ mapTitle: title, isDirty: true }),
  setDirty: (dirty) => set({ isDirty: dirty }),
  setCanvasBackground: (color) => set({ canvasBackground: color }),

  addMessage: (nodeId, message) => set(state => ({
    nodeChats: {
      ...state.nodeChats,
      [nodeId]: [...(state.nodeChats[nodeId] || []), message]
    }
  })),

  setNodeMastery: (nodeId, level) => set(state => ({
    nodeMastery: { ...state.nodeMastery, [nodeId]: level },
    nodes: state.nodes.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, mastery: level } } : n
    ),
    isDirty: true
  })),

  addNode: (parentId, label, nodeType = 'child') => {
    const state = get();
    const parent = state.nodes.find(n => n.id === parentId);
    if (!parent) return;
    // Count existing children to offset position
    const siblingCount = state.edges.filter(e => e.source === parentId).length;
    const angleOffset = siblingCount * 60;
    const rad = (angleOffset * Math.PI) / 180;
    const dist = 220;
    const id = `node-${Date.now()}`;
    const newNode = {
      id, type: 'atlasNode',
      position: {
        x: parent.position.x + Math.cos(rad) * dist,
        y: parent.position.y + Math.sin(rad) * dist + 80
      },
      data: { label, description: '', mastery: 'none', nodeType }
    };
    const newEdge = {
      id: `edge-${Date.now()}`,
      source: parentId, target: id,
      sourceHandle: 'h-right', targetHandle: 't-left',
      style: { stroke: '#0047AB' }
    };
    set({ nodes: [...state.nodes, newNode], edges: [...state.edges, newEdge], isDirty: true });
  },

  deleteNode: (id) => set(state => ({
    nodes: state.nodes.filter(n => n.id !== id),
    edges: state.edges.filter(e => e.source !== id && e.target !== id),
    selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
    sidebarTab: state.selectedNodeId === id ? null : state.sidebarTab,
    isDirty: true
  })),

  updateNodeData: (id, newData) => set(state => ({
    nodes: state.nodes.map(n =>
      n.id === id ? { ...n, data: { ...n.data, ...newData } } : n
    ),
    isDirty: true
  })),

  updateEdgeColor: (edgeId, color) => set(state => ({
    edges: state.edges.map(e =>
      e.id === edgeId ? { ...e, style: { ...e.style, stroke: color } } : e
    ),
    isDirty: true
  })),

  deleteEdge: (edgeId) => set(state => ({
    edges: state.edges.filter(e => e.id !== edgeId),
    selectedEdgeId: state.selectedEdgeId === edgeId ? null : state.selectedEdgeId,
    isDirty: true
  })),

  loadMap: (mapId, title, nodes, edges, chats, mastery) => set({
    mapId, mapTitle: title, nodes, edges,
    nodeChats: chats || {}, nodeMastery: mastery || {},
    isDirty: false, selectedNodeId: null, selectedEdgeId: null, sidebarTab: null
  }),

  resetMap: () => set({
    mapId: null, mapTitle: '', nodes: [], edges: [],
    selectedNodeId: null, selectedEdgeId: null, sidebarTab: null,
    nodeChats: {}, nodeMastery: {}, isDirty: false, canvasBackground: '#ffffff'
  }),
}));

export default useCanvasStore;
